/**
 * SuggestionBoxWidget — floating feedback button for The Loom writer workspace.
 *
 * Renders a small round "?" button fixed to the bottom-right corner of the
 * workspace. Clicking it opens a compact popover where the writer can type
 * a message and submit it to the Crown & Rose suggestions inbox via
 * POST /api/feedback.
 */
import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function SuggestionBoxWidget() {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const textareaRef = useRef(null);
  const panelRef = useRef(null);

  // Focus textarea when panel opens
  useEffect(() => {
    if (open && !sent) {
      setTimeout(() => textareaRef.current?.focus(), 80);
    }
  }, [open, sent]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    // Small delay so the button-click that opens it doesn't immediately close
    const timer = setTimeout(() => document.addEventListener('mousedown', handler), 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handler);
    };
  }, [open]);

  const handleClose = () => {
    setOpen(false);
    // Reset after animation
    setTimeout(() => { setMessage(''); setSent(false); }, 300);
  };

  const handleSend = async () => {
    const trimmed = message.trim();
    if (!trimmed) return;
    setSending(true);
    try {
      const resp = await fetch(`${API_BASE}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: trimmed,
          context: window.__loomCurrentPanel || null,
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setSent(true);
      setTimeout(handleClose, 2400);
    } catch (err) {
      toast.error('Could not send feedback. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed bottom-12 right-4 z-50 flex flex-col items-end gap-2"
      data-testid="suggestion-box-container"
    >
      {/* Feedback panel */}
      {open && (
        <div
          ref={panelRef}
          className="
            w-72 rounded-xl border border-border bg-background
            shadow-2xl shadow-black/20
            animate-in fade-in slide-in-from-bottom-2 duration-200
          "
          data-testid="suggestion-box-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-rose-700" />
              <span className="text-sm font-semibold text-foreground">Share feedback</span>
            </div>
            <button
              onClick={handleClose}
              className="rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              data-testid="suggestion-box-close-btn"
              aria-label="Close feedback panel"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Body */}
          <div className="px-4 py-3">
            {sent ? (
              <div
                className="flex flex-col items-center gap-2 py-4 text-center"
                data-testid="suggestion-box-success"
              >
                <CheckCircle className="h-8 w-8 text-emerald-500" />
                <p className="text-sm font-medium text-foreground">Thank you!</p>
                <p className="text-xs text-muted-foreground">
                  Your feedback has been sent to the Crown &amp; Rose team.
                </p>
              </div>
            ) : (
              <>
                <p className="text-xs text-muted-foreground mb-2">
                  Idea, issue, or suggestion? We read every message.
                </p>
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSend();
                  }}
                  placeholder="What's on your mind..."
                  rows={4}
                  maxLength={4000}
                  disabled={sending}
                  className="
                    w-full resize-none rounded-md border border-input bg-background
                    px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground
                    focus:outline-none focus:ring-1 focus:ring-rose-700/60
                    disabled:opacity-50 transition-colors
                  "
                  data-testid="suggestion-box-textarea"
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[10px] text-muted-foreground">
                    {message.length > 0 ? `${message.length}/4000` : '\u2318\u21a9 to send'}
                  </span>
                  <button
                    onClick={handleSend}
                    disabled={!message.trim() || sending}
                    className="
                      flex items-center gap-1.5 rounded-md bg-rose-800 px-3 py-1.5
                      text-xs font-semibold text-white
                      hover:bg-rose-700 active:bg-rose-900
                      disabled:opacity-40 disabled:cursor-not-allowed
                      transition-colors
                    "
                    data-testid="suggestion-box-send-btn"
                  >
                    {sending ? (
                      <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                      </svg>
                    ) : (
                      <Send className="h-3 w-3" />
                    )}
                    {sending ? 'Sending…' : 'Send'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Trigger button */}
      <button
        onClick={() => setOpen((v) => !v)}
        className={`
          h-8 w-8 rounded-full shadow-lg border border-border
          flex items-center justify-center
          text-xs font-bold transition-all duration-200
          ${open
            ? 'bg-rose-800 text-white border-rose-800 scale-95'
            : 'bg-background text-muted-foreground hover:bg-rose-800 hover:text-white hover:border-rose-800 hover:scale-105'}
        `}
        title="Share feedback"
        aria-label="Open suggestion box"
        data-testid="suggestion-box-trigger-btn"
      >
        ?
      </button>
    </div>
  );
}
